/**
 * Small structured-data helpers used by legacy page-components.
 *
 * 2026-09: rewritten without the "save up to 80%" descriptions, the
 * MedicalBusiness type and the pharmacy rating generator. The canonical
 * Organization and WebSite nodes live in SiteWideSchema.tsx; the objects
 * here are kept only because legacy pages still pass them to the (no-op)
 * EnhancedSEOHead component.
 */
const SITE = 'https://www.scriptunlock.com';

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE}/#organization`,
  name: 'Script Unlock',
  url: SITE,
  logo: `${SITE}/logo-icon-512.png`,
  description:
    'A US marketplace where a patient uploads a prescription and licensed, verified pharmacies each respond with their own cash price.',
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE}/#website`,
  name: 'Script Unlock',
  url: SITE,
  publisher: { '@id': `${SITE}/#organization` },
};

export const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to get pharmacies to quote a cash price on your prescription',
  description: 'Upload a prescription, let licensed pharmacies respond with their own cash price, choose one and pay it directly.',
  step: [
    { '@type': 'HowToStep', name: 'Upload your prescription', text: 'Take a photo of your prescription or upload the file.' },
    { '@type': 'HowToStep', name: 'Pharmacies respond', text: 'Licensed, verified pharmacies see the request and each can reply with a cash price for the exact drug, strength and quantity.' },
    { '@type': 'HowToStep', name: 'Choose a pharmacy', text: 'Compare the responses and pick the pharmacy that suits you.' },
    { '@type': 'HowToStep', name: 'Pay at the pharmacy', text: 'Collect your medication and pay the pharmacy directly at the price it quoted.' },
  ],
};

export const generateFAQSchema = (faqs: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  })),
});

export const generateBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url.startsWith('http') ? item.url : `${SITE}${item.url}`,
  })),
});

export const generateHowToSchema = (config: {
  name: string;
  description: string;
  steps: { name: string; text: string }[];
  totalTime?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: config.name,
  description: config.description,
  ...(config.totalTime ? { totalTime: config.totalTime } : {}),
  step: config.steps.map((s) => ({ '@type': 'HowToStep', name: s.name, text: s.text })),
});
